import { useState, useEffect } from 'react';

const PasswordGenerator = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(11);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [strength, setStrength] = useState(0);

  const generatePassword = () => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      setPassword('');
      return;
    }

    let newPassword = '';
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(newPassword);
    calculateStrength(newPassword);
  };

  const calculateStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;
    setStrength(score);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
  };

  useEffect(() => {
    generatePassword();
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  if (!isOpen) return null;

  const getStrengthColor = () => {
    if (strength <= 1) return 'bg-red-500';
    if (strength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-[90%] max-w-[290px] relative shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-medium text-inherit leading-[1.1] mt-0 mb-4 m-0 font-['Bahnschrift_Light'] text-center">Advance Password Generator</h2>

        <div className="mb-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-mono text-gray-700 flex-1 break-words max-w-[calc(100%-80px)]">{password || 'Generate Password'}</span>
            <button 
              className="ml-3 text-gray-600 hover:text-teal-600 text-lg font-black [font-family:'Font_Awesome_5_Free'] [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale] inline-block [font-style:normal] [font-variant:normal] [text-rendering:auto] leading-none text-center w-[1.25em]"
              onClick={generatePassword}
            >
              <i className="fa fa-fw fa-repeat"></i>
            </button>
            <button 
              className="ml-2 text-gray-600 hover:text-teal-600 text-lg font-black [font-family:'Font_Awesome_5_Free'] [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale] inline-block [font-style:normal] [font-variant:normal] [text-rendering:auto] leading-none text-center w-[1.25em]"
              onClick={copyToClipboard}
            >
              <i className="fa fa-fw fa-copy"></i>
            </button>
          </div>

          <div className="flex gap-1 mt-2">
            {[...Array(5)].map((_, index) => (
              <div 
                key={index}
                className={`h-[3px] flex-1 ${index < strength ? getStrengthColor() : 'bg-gray-200'}`}
              />
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-600 text-base mb-2 text-center">Length ({length})</label>
          <input 
            type="range"
            min="4"
            max="32"
            value={length}
            onChange={(e) => setLength(parseInt(e.target.value))}
            style={{
              background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((length - 4) / (32 - 4)) * 100}%, #4a4a4a ${((length - 4) / (32 - 4)) * 100}%, #4a4a4a 100%)`
            }}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#14b8a6] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#14b8a6] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center justify-between cursor-pointer inline-block max-w-[80%] mb-0 font-bold">
            <span className="text-gray-600 text-[14px]">Uppercase</span>
            <input 
              type="checkbox"
              checked={includeUppercase}
              onChange={(e) => setIncludeUppercase(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#ffeb00] cursor-pointer ml-2 mb-0 mt-0 leading-normal box-border p-0"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer inline-block max-w-[80%] mb-0 font-bold">
            <span className="text-gray-600 text-[14px]">Numbers</span>
            <input 
              type="checkbox"
              checked={includeNumbers}
              onChange={(e) => setIncludeNumbers(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#ffeb00] cursor-pointer ml-2 mb-0 mt-0 leading-normal box-border p-0"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer inline-block max-w-[80%] mb-0 font-bold">
            <span className="text-gray-600 text-[14px]">Lowercase</span>
            <input 
              type="checkbox"
              checked={includeLowercase}
              onChange={(e) => setIncludeLowercase(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#ffeb00] cursor-pointer ml-2 mb-0 mt-0 leading-normal box-border p-0"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer inline-block max-w-[80%] mb-0 font-bold">
            <span className="text-gray-600 text-[14px]">Symbols</span>
            <input 
              type="checkbox"
              checked={includeSymbols}
              onChange={(e) => setIncludeSymbols(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#ffeb00] cursor-pointer ml-2 mb-0 mt-0 leading-normal box-border p-0"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default PasswordGenerator;
